import { ActionsObservable } from 'redux-observable';
import { Observable } from 'rxjs';

import Organization from 'entities/organization/model';
import { showMessageAction, showErrorAction } from 'containers/Notification/actions';
import PartnersApi from 'entities/partners/api';
import OrganizationApi from 'entities/organization/api';
import { selectOrganizationByIdOrExternalId } from 'entities/organization/selectors';
import { addAction } from 'entities/organization/types';

import {
  FetchParams,
  Init,
  INIT,
  initFailed,
  initSuccess,
  fetchDeedsTotalSuccess,
  fetchDeedsTotalFailed,
  fetchDonationsTotalSuccess,
  fetchDonationsTotalFailed,
  fetchVolunteerTimesTotalSuccess,
  fetchVolunteerTimesTotalFailed,
  fetchPartnersSuccessAction,
  fetchPartnersFailedAction,
  createPartnerSuccessAction,
  createPartnerFailedAction,
  FETCH_DONATIONS_TOTAL,
  FETCH_DEEDS_TOTAL,
  FETCH_VOLUNTEERHOURS_TOTAL,
  FETCH_PARTNERS,
  CREATE_PARTNER,
  DELETE_PARTNER,
  fetchPartnersAction,
  deletePartnerSuccessAction,
  deletePartnerFailedAction,
} from './types';
import { selectFetchParams } from './selectors';

const fetch = (action$: ActionsObservable<Init | FetchParams>, store: any) =>
  action$.ofType(INIT).mergeMap(() => {
    const state = store.getState();
    const fetchParams = selectFetchParams(state);
    const id = fetchParams.get('id');

    const selectedOrganization = selectOrganizationByIdOrExternalId(state, id);

    if (selectedOrganization) {
      return [initSuccess()];
    }

    return OrganizationApi.fetchNonprofit(id)
      .mergeMap((organization) => [addAction(new Organization(organization)), initSuccess()])
      .takeUntil(action$.ofType(INIT))
      .catch((e) => [initFailed(e)]);
  });

const fetchDonationsTotal = (action$) =>
  action$
    .ofType(FETCH_DONATIONS_TOTAL)
    .mergeMap(({ id }) =>
      OrganizationApi.fetchCompanyDonationsTotalById(id).flatMap((totals) => [
        fetchDonationsTotalSuccess(totals?.[0]?.totalAmount || 0),
      ])
    )
    .catch((e) => [fetchDonationsTotalFailed(e)]);

const fetchDeedsTotal = (action$) =>
  action$
    .ofType(FETCH_DEEDS_TOTAL)
    .mergeMap(({ id }) =>
      OrganizationApi.fetchCompanyDeedsTotalById(id).flatMap((totals) => [
        fetchDeedsTotalSuccess(totals?.[0]?.count || 0),
      ])
    )
    .catch((e) => [fetchDeedsTotalFailed(e)]);

const fetchVolunteerTimesTotal = (action$) =>
  action$
    .ofType(FETCH_VOLUNTEERHOURS_TOTAL)
    .mergeMap(({ id }) =>
      OrganizationApi.fetchCompanyVolunteerTimesTotalById(id).flatMap((totals) => [
        fetchVolunteerTimesTotalSuccess(totals?.[0]?.duration || 0),
      ])
    )
    .catch((e) => [fetchVolunteerTimesTotalFailed(e)]);

const fetchPartners = (action$: any) =>
  action$
    .ofType(FETCH_PARTNERS)
    .mergeMap(
      ({
        search,
        countryCode,
        acceptsDonations,
      }: {
        search: any;
        countryCode: any;
        acceptsDonations: any;
      }) =>
        PartnersApi.fetch({ search, countryCode, acceptsDonations })
          .mergeMap((resultingAction) => [resultingAction, fetchPartnersSuccessAction()])
          .catch((e) => Observable.of(fetchPartnersFailedAction(e)))
    );

const createPartner = (action$: any) =>
  action$.ofType(CREATE_PARTNER).mergeMap(({ id }: { id: string }) =>
    Observable.from(PartnersApi.create(id))
      .mergeMap((action) => [
        action,
        createPartnerSuccessAction(),
        fetchPartnersAction(),
        showMessageAction('Partnership added!'),
      ])
      .catch((e) =>
        Observable.of(
          createPartnerFailedAction(e),
          showErrorAction('An error occurred saving partnership.')
        )
      )
  );

const deletePartner = (action$: any) =>
  action$.ofType(DELETE_PARTNER).mergeMap(({ partner }: { partner: string }) =>
    Observable.from(PartnersApi.delete(partner))
      .mergeMap((action) => [
        action,
        deletePartnerSuccessAction(),
        fetchPartnersAction(),
        showMessageAction('Partnership removed!'),
      ])
      .catch((e) =>
        Observable.of(
          deletePartnerFailedAction(e),
          showErrorAction('An error occurred removing partnership.')
        )
      )
  );

export default [
  fetch,
  fetchDonationsTotal,
  fetchDeedsTotal,
  fetchVolunteerTimesTotal,
  fetchPartners,
  createPartner,
  deletePartner,
];
